import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { t } from '../i18n'
import { mappingOf } from '../lib/gerber'
import type { GerberFile, LayerSide, LayerType } from '../types'

const COLLAPSED_KEY = 'pcba.layers.collapsed'

function readCollapsed(): boolean {
  try {
    // Default to the slim tab; expanding once is remembered per browser.
    return window.localStorage.getItem(COLLAPSED_KEY) !== '0'
  } catch {
    return true
  }
}

interface Props {
  files: GerberFile[]
  onToggle: (id: string, enabled: boolean) => void
}

const TYPE_COLOR: Record<string, string> = {
  copper: 'var(--copper)',
  soldermask: 'var(--mask)',
  silkscreen: 'var(--silk)',
  solderpaste: 'var(--paste)',
  drill: 'var(--drill)',
  outline: 'var(--outline)',
  drawing: 'var(--muted)',
}

const SIDED_TYPES = new Set<LayerType>(['copper', 'soldermask', 'silkscreen', 'solderpaste'])

function sideRank(side: LayerSide): number {
  if (side === 'top') return 0
  if (side === 'inner') return 1
  if (side === 'bottom') return 2
  return 3
}

function stackRank(file: GerberFile): number {
  const type = mappingOf(file).type
  const side = mappingOf(file).side
  if (type === 'outline') return 0
  if (type === 'drill') return 40
  if (!type || type === 'drawing') return 50
  if (type === 'silkscreen') return side === 'bottom' ? 33 : 10
  if (type === 'solderpaste') return side === 'bottom' ? 32 : 11
  if (type === 'soldermask') return side === 'bottom' ? 31 : 12
  if (type === 'copper') {
    if (side === 'top') return 13
    if (side === 'bottom') return 30
    return 20
  }
  return 25
}

function layerText(file: GerberFile): string {
  const mapping = mappingOf(file)
  const type = mapping.type ? t(`layer.type.${mapping.type}`) : t('layers.unmapped')
  if (!mapping.type || !SIDED_TYPES.has(mapping.type) || !mapping.side) return type
  return `${type} · ${t(`layer.side.${mapping.side}`)}`
}

export function LayerRail({ files, onToggle }: Props) {
  const ordered = useMemo(
    () =>
      [...files].sort(
        (a, b) =>
          stackRank(a) - stackRank(b) ||
          sideRank(mappingOf(a).side) - sideRank(mappingOf(b).side) ||
          a.name.localeCompare(b.name),
      ),
    [files],
  )

  const copperNumber = useMemo(() => {
    const result = new Map<string, number>()
    let index = 0
    for (const file of ordered) {
      if (mappingOf(file).type !== 'copper') continue
      index += 1
      result.set(file.id, index)
    }
    return result
  }, [ordered])

  const labelCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const file of ordered) {
      const label = layerText(file)
      counts.set(label, (counts.get(label) ?? 0) + 1)
    }
    return counts
  }, [ordered])

  const onCount = files.filter((file) => file.enabled).length
  const [collapsed, setCollapsed] = useState(readCollapsed)

  function setRailCollapsed(value: boolean): void {
    setCollapsed(value)
    try {
      window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0')
    } catch {
      // Private mode or storage disabled: the rail still works for this view.
    }
  }

  if (collapsed) {
    return (
      <aside className="layer-rail is-collapsed" aria-label={t('layers.title')}>
        <button
          className="layer-rail-expand"
          type="button"
          aria-label={t('layers.expand')}
          title={t('layers.expand')}
          aria-expanded="false"
          onClick={() => setRailCollapsed(false)}
        >
          <ChevronLeft size={12} />
          <span className="layer-rail-expand-label">{t('layers.title')}</span>
          <span className="layer-rail-expand-count">{onCount}/{files.length}</span>
        </button>
      </aside>
    )
  }

  return (
    <aside className="layer-rail" aria-label={t('layers.title')}>
      <header className="layer-rail-head">
        <strong>{t('layers.title')}</strong>
        <span>{onCount}/{files.length}</span>
        <button
          className="layer-rail-collapse"
          type="button"
          aria-label={t('layers.collapse')}
          title={t('layers.collapse')}
          aria-expanded="true"
          onClick={() => setRailCollapsed(true)}
        >
          <ChevronRight size={12} />
        </button>
      </header>
      <div className="layer-rail-stack">{t('layers.stack')}</div>
      <div className="layer-rail-list">
        {ordered.map((file, index) => {
          const mapping = mappingOf(file)
          const copper = copperNumber.get(file.id)
          const baseLabel = layerText(file)
          const duplicate = (labelCounts.get(baseLabel) ?? 0) > 1
          const extension = file.name.includes('.') ? file.name.split('.').pop()?.toUpperCase() : ''
          const label = duplicate && !copper && extension ? `${baseLabel} · ${extension}` : baseLabel
          return (
            <button
              key={file.id}
              className={`layer-rail-row ${file.enabled ? '' : 'is-off'}`}
              type="button"
              aria-pressed={file.enabled}
              aria-label={t('layers.toggle', { name: file.name })}
              title={file.name}
              onClick={() => onToggle(file.id, !file.enabled)}
            >
              <span className="layer-rail-num">{String(index + 1).padStart(2, '0')}</span>
              <span
                className="layer-rail-dot"
                style={{ background: TYPE_COLOR[mapping.type ?? 'drawing'] ?? 'var(--muted)' }}
              />
              <span className="layer-rail-label">
                {copper ? <em>L{copper}</em> : null}
                {label}
              </span>
              <span className={`layer-rail-switch ${file.enabled ? 'is-on' : ''}`} aria-hidden="true">
                <span />
              </span>
            </button>
          )
        })}
      </div>
    </aside>
  )
}
